/* eslint-disable @typescript-eslint/no-explicit-any */
import { faPlus, faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Modal } from 'react-bootstrap';
import {
  useForm,
  Controller,
  FieldValues,
  SubmitHandler
} from 'react-hook-form';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useSelector, useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Button from 'components/base/Button';
import { showAlert } from 'store/alert/alert.thunk';
import orderService from 'service/order';
import NewApplicationDataTable from './NewApplicationDataTable';
import customerEntityService from 'service/charterer';
import providerEntityService from 'service/providerEntity';

const CreateNewApplication = () => {
  const { t, i18n } = useTranslation();
  const {
    control,
    handleSubmit,
    setValue,
    register,
    formState: { errors }
  } = useForm();
  const dispatch: Dispatch<any> = useDispatch();
  const cashboxId = useSelector((state: any) => state?.shiftId?.shiftId);
  const cashboxPid = useSelector((state: any) => state?.shiftId?.shiftPid);
  const navigate = useNavigate();

  const legalEntityId = useSelector(
    (state: any) => state?.auth?.legal_entity?.id
  );
  const legalEntityType = useSelector(
    (state: any) => state?.auth?.legal_entity?.legal_entity_type
  );

  const [legalEntityOption, setLegalEntityOption] = useState<any>([]);
  const [openFolder, setOpenFolder] = useState(false);
  const [selectedLegalEntityId, setSelectedLegalEntityId] = useState<any>(0);
  const [loading, setLoading] = useState(false);
  const [currencyOption, setCurrencyOption] = useState<any>([]);

  //GET LEgal Entity to options
  useQuery(['GET_LEGAL_ENTITY'], async () => {
    if (legalEntityType) {
      await providerEntityService
        .getList({
          offset: 0,
          limit: 100,
          legal_entity_type: legalEntityType,
          is_verified_supplier: true
        })
        .then((res: any) => {
          const options = res?.legal_entities?.map((option: any) => ({
            value: option?.pid || 0,
            currency_pid: option?.currency?.pid || 0,
            address: option?.address || '',
            l_lat: option?.l_lat || 0,
            l_long: option?.l_long || 0,
            label: option.name,
            currencies: option?.currencies || []
          }));

          setLegalEntityOption(options);
        });
    }
  });

  const { data: chartererData } = useQuery(
    ['GET_CHARTERER_LIMIT', selectedLegalEntityId, legalEntityId],
    () => {
      if (selectedLegalEntityId !== '' && legalEntityId !== '') {
        const resData = customerEntityService
          .getByIdCheckChartererLimit(selectedLegalEntityId, legalEntityId, {
            limit_type: 'TIME'
          })
          .then((res: any) => {
            return res;
          });

        return resData;
      }
    },
    { enabled: !!selectedLegalEntityId && !!legalEntityId }
  );

  const onSubmit: SubmitHandler<FieldValues> = data => {
    if (!cashboxId || !cashboxPid) {
      dispatch(showAlert({ title: t('shift_not_open') }));
      return;
    }

    if (chartererData?.id) {
      dispatch(showAlert({ title: t('you_have_time_limit') }));
      return;
    }

    const selectedSender = legalEntityOption.find(
      (item: any) => Number(item?.value) === Number(data?.sender)
    );

    if (data?.sender !== 0 && cashboxPid !== 0) {
      setLoading(true);
      orderService
        .createOrder({
          sender_pid: +data?.sender || 0,
          cashbox_pid: +cashboxPid || 0,
          recipient_law_subject_type: 1,
          currency_pid: +data?.currency || 0,
          address: selectedSender?.address || '',
          lat: selectedSender?.l_lat || 0,
          long: selectedSender?.l_long || 0
        })
        .then((res: any) => {
          if (res) {
            setLoading(false);
            dispatch(
              showAlert({
                title: `${t('applications')} ${t('successfully_added')}`,
                type: 'success'
              })
            );
            navigate(
              `/applications/outgoing/filter/newapplications/${res?.pid}`,
              {
                state: {
                  sender_pid: data?.sender,
                  currency_pid: +data?.currency
                }
              }
            );
          }
        });
    }
  };

  return (
    <Form
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Form.Group className="md-4 mb-2">
        <div className="react-select-container">
          <Controller
            name="sender"
            control={control}
            defaultValue=""
            rules={{ required: true }}
            render={({ field }) => (
              <Form.Floating>
                <Form.Select
                  {...field}
                  isInvalid={!!errors.sender}
                  onChange={(e: any) => {
                    const selectedOption = legalEntityOption.find(
                      (option: any) =>
                        Number(option.value) === Number(e.target.value)
                    );
                    if (selectedOption) {
                      const options = selectedOption?.currencies?.map(
                        (option: any) => ({
                          value: option?.pid || 0,
                          label: option?.name?.[i18n?.language]
                        })
                      );
                      setCurrencyOption(options);
                      setSelectedLegalEntityId(selectedOption?.value);
                      setValue('address', selectedOption?.address);

                      const mainCurrency = selectedOption?.currencies?.find(
                        (currency: any) => currency?.is_main === true
                      );
                      setValue(
                        'currency',
                        mainCurrency?.pid || selectedOption?.currency_pid
                      );
                    }
                    field.onChange(e);
                  }}
                >
                  <option className="d-none" value=""></option>
                  {legalEntityOption?.map((option: any) => (
                    <option
                      key={option.value}
                      value={option.value}
                      className="option"
                    >
                      {option.label}
                    </option>
                  ))}
                </Form.Select>

                <label htmlFor="floatingInputCustom">{t('sender')}</label>
                {errors?.sender && (
                  <span
                    style={{
                      marginTop: '2px',
                      color: 'red',
                      fontSize: '12px'
                    }}
                  >
                    {t('required_field')}
                  </span>
                )}
              </Form.Floating>
            )}
          />
        </div>
      </Form.Group>
      <Form.Group className="md-4 mb-2">
        <div className="react-select-container">
          <Controller
            name="currency"
            control={control}
            defaultValue=""
            rules={{ required: true }}
            render={({ field }) => (
              <Form.Floating>
                <Form.Select
                  {...field}
                  isInvalid={!!errors.currency}
                  onChange={(e: any) => {
                    const selectedOption = currencyOption.find(
                      (option: any) =>
                        Number(option.value) === Number(e.target.value)
                    );
                    if (selectedOption) {
                      setSelectedLegalEntityId(selectedOption?.value);
                    }
                    field.onChange(e);
                  }}
                >
                  <option className="d-none" value=""></option>
                  {currencyOption?.map((option: any) => (
                    <option
                      key={option.value}
                      value={option.value}
                      className="option"
                    >
                      {option.label}
                    </option>
                  ))}
                </Form.Select>

                <label htmlFor="floatingInputCustom">{t('currency')}</label>
                {errors?.currency && (
                  <span
                    style={{
                      marginTop: '2px',
                      color: 'red',
                      fontSize: '12px'
                    }}
                  >
                    {t('required_field')}
                  </span>
                )}
              </Form.Floating>
            )}
          />
        </div>
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Floating className="md-4">
          <Form.Control
            disabled
            {...register('address')}
            type="text"
            placeholder="address"
            onFocus={e => e.target.select()}
          />
          <label htmlFor="floatingInputCustom">{t('address')}</label>
        </Form.Floating>
      </Form.Group>

      <Form.Group style={{ display: 'flex', gap: '10px', marginTop: 'auto' }}>
        <Button
          variant="outline-primary"
          type="button"
          style={{ height: '48px', width: '100%' }}
          onClick={() => setOpenFolder(true)}
        >
          <FontAwesomeIcon icon={faFolderOpen} className="me-2" />
          {t('open')}
        </Button>
        <Button
          variant="primary"
          type="submit"
          style={{ height: '48px', width: '100%' }}
          loading={loading}
          loadingPosition="start"
        >
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          {t('create')}
        </Button>
      </Form.Group>

      <Modal
        show={openFolder}
        onHide={() => setOpenFolder(false)}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {t('select_application')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <NewApplicationDataTable setOpenFolder={setOpenFolder} />
        </Modal.Body>
      </Modal>
    </Form>
  );
};

export default CreateNewApplication;
